import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Menu, X, Briefcase } from "lucide-react";
import { useState } from "react";
import CookieBanner from "@/components/CookieBanner";
import { FlagsBENL } from "@/components/Flags";
import { usePublicConfig } from "@/lib/usePublicConfig";

const BASE_NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Alle checks", href: "/checks" },
  { label: "Hoe het werkt", href: "/hoe-het-werkt" },
  { label: "Prijzen", href: "/prijzen" },
  { label: "Blog", href: "/blog", flag: "blog_enabled" },
  { label: "Over ons", href: "/over-ons" },
  { label: "Contact", href: "/contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const cfg = usePublicConfig();
  const blogEnabled = cfg?.blog_enabled !== false;
  const NAV_LINKS = BASE_NAV_LINKS.filter((l) => !l.flag || (l.flag === "blog_enabled" && blogEnabled));

  function gotoChecks(e) {
    e?.preventDefault?.();
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById("alle-checks");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate("/checks");
  }

  // Hide chrome on admin routes
  if (location.pathname.startsWith("/admin")) {
    return <>{children}</>;
  }

  return (
    <div className="App min-h-screen flex flex-col bg-background">
      <div className="ribbon text-white text-xs font-medium" data-testid="top-ribbon">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-2">
            CheckExpert — Weet wat eerlijk is. Betaal wat eerlijk is.
          </span>
          <span className="hidden sm:inline-flex items-center gap-2 opacity-90">
            <FlagsBENL size="w-4 h-3" />
            <span className="opacity-80">Voor België &amp; Nederland · GDPR-conform · Resultaat in 60 sec</span>
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-40 glass-nav">
        <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-[#002FA7] text-white shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-bold tracking-tight text-ink">
              Check<span className="text-[#002FA7]">Expert</span>
            </span>
          </Link>

          <ul className="hidden md:flex items-center gap-7 text-sm font-medium text-ink-soft">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  className={`hover:text-ink transition-colors ${location.pathname === l.href ? "text-ink" : ""}`}
                  data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <Link
              to="/voor-bedrijven"
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-ink-soft hover:text-ink hover:border-ink-soft transition-colors"
              data-testid="nav-b2b"
            >
              <Briefcase className="h-3.5 w-3.5" /> Voor bedrijven
            </Link>
            <a
              href="/#alle-checks"
              onClick={gotoChecks}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] transition-colors text-white text-sm font-semibold shadow-sm"
              data-testid="cta-nav-start"
            >
              Start een check
            </a>
            <button
              className="md:hidden p-2 rounded-md text-ink"
              onClick={() => setOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        {open && (
          <div className="md:hidden border-t border-border bg-white/95" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-3">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="block text-ink-soft text-sm font-medium"
                  onClick={() => setOpen(false)}
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/voor-bedrijven" onClick={() => setOpen(false)} className="block text-ink-soft text-sm font-medium">
                Voor bedrijven
              </Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="block text-ink-soft text-sm font-medium">
                Contact
              </Link>
              <a
                href="/#alle-checks"
                onClick={gotoChecks}
                className="inline-flex items-center justify-center w-full px-5 py-2.5 rounded-full bg-[#002FA7] text-white text-sm font-semibold"
              >
                Start een check
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" key={location.pathname}>
        {children}
      </main>

      <footer className="bg-slate-900 text-slate-300" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/10 text-white">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <span className="font-display text-xl font-bold tracking-tight text-white">
                Check<span className="text-blue-300">Expert</span>
              </span>
            </div>
            <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
              CheckExpert helpt particulieren in België en Nederland om in 60 seconden te weten wat er écht in hun contract of document staat — in heldere taal en zonder dure tussenpersoon.
            </p>
            <p className="mt-4 text-xs text-slate-400 inline-flex items-center gap-2">
              <FlagsBENL size="w-4 h-3" /> <span>Actief in België &amp; Nederland</span>
            </p>
            <p className="mt-6 text-xs text-slate-500">
              Webtron BV · A. Rodenbachwijk 12, 8792 Waregem · BE0793944703
            </p>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Platform</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><Link to="/checks" className="hover:text-white" data-testid="footer-checks">Alle checks</Link></li>
              <li><Link to="/hoe-het-werkt" className="hover:text-white">Hoe het werkt</Link></li>
              <li><Link to="/prijzen" className="hover:text-white">Prijzen</Link></li>
              <li><Link to="/credits" className="hover:text-white">Mijn credits</Link></li>
              <li><Link to="/mijn-checks" className="hover:text-white" data-testid="footer-mijn-checks">Mijn checks</Link></li>
              {blogEnabled && <li><Link to="/blog" className="hover:text-white">Blog</Link></li>}
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Bedrijf</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/over-ons" className="hover:text-white">Over ons</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/voor-bedrijven" className="hover:text-white">Voor bedrijven</Link></li>
              <li><Link to="/sitemap" className="hover:text-white" data-testid="footer-sitemap">Sitemap</Link></li>
              <li><a href="mailto:info@checkexpert.be" className="hover:text-white">info@checkexpert.be</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Juridisch</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/algemene-voorwaarden" className="hover:text-white">Voorwaarden</Link></li>
              <li><Link to="/cookiebeleid" className="hover:text-white">Cookiebeleid</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-slate-500 flex flex-col md:flex-row gap-2 justify-between">
            <span>© {new Date().getFullYear()} CheckExpert — een product van <a href="https://webtron.be" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-4 hover:underline">Webtron BV</a>. Alle rechten voorbehouden.</span>
            <span>CheckExpert vervangt geen juridisch, financieel of medisch advies. Bij twijfel: raadpleeg een specialist.</span>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
